import { Button, ButtonGroup, Card, InlineStack, Page, Tabs } from '@shopify/polaris';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApprovalSummaryStore, useLookupStore } from '../../context/useStore';
import Toasts from '../../shared/components/toasts/toasts';
import { useUrl } from '../../shared/util/hanldesave';
import { DOC_TYPES } from '../../shared/components/approvals/approvalsApi';
import IntakeCard from './components/IntakeCard';
import QueueShell from './components/QueueShell';
import { TabTitle } from '../../shell/helmet.jsx';

const LANES = [
  { key: 'unprocessed', label: 'New', countKey: 'unprocessed' },
  { key: 'to_assign', label: 'To assign', countKey: 'toAssign' },
  { key: 'rejected', label: 'Rejected', countKey: 'rejected' }
];

// Accounting's intake: new Peppol / email documents, processed documents without an approver, rejected documents.
export default function IntakeQueuePage() {
  const url = useUrl();
  const navigate = useNavigate();
  const summary = useApprovalSummaryStore((state) => state.summary);
  const refreshSummary = useApprovalSummaryStore((state) => state.refresh);
  const lookups = useLookupStore((state) => ({
    setPurchasetypes: state.setPurchasetypes,
    setBusinessunits: state.setBusinessunits,
    setCurrencies: state.setCurrencies,
    setPaymentconditions: state.setPaymentconditions
  }));
  const [tab, setTab] = useState(Math.min(1, Math.max(0, parseInt(url.get('tab') || '0', 10) || 0)));
  const [lane, setLane] = useState(LANES.some((l) => l.key === url.get('lane')) ? url.get('lane') : 'unprocessed');
  const [toast, setToast] = useState(false);
  const [toastError, setToastError] = useState(false);
  const initialId = url.get('id');

  useEffect(() => {
    lookups.setPurchasetypes();
    lookups.setBusinessunits();
    lookups.setCurrencies();
    lookups.setPaymentconditions();
    refreshSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const docType = DOC_TYPES[tab] || 'invoice';
  const counts = summary?.intake?.[docType === 'invoice' ? 'invoices' : 'creditnotes'] || {};

  const tabs = useMemo(() => {
    const total = (key) => {
      const c = summary?.intake?.[key];
      return c ? c.unprocessed + c.toAssign + c.rejected : 0;
    };
    return [
      { id: 'intake-invoices', content: `Invoices${total('invoices') ? ` (${total('invoices')})` : ''}`, panelID: 'intake-invoices' },
      { id: 'intake-creditnotes', content: `Credit notes${total('creditnotes') ? ` (${total('creditnotes')})` : ''}`, panelID: 'intake-creditnotes' }
    ];
  }, [summary]);

  const update = useCallback(
    (nextTab, nextLane) => {
      setTab(nextTab);
      setLane(nextLane);
      navigate({ pathname: '/approvals/intake', search: `?tab=${nextTab}&lane=${nextLane}` }, { replace: true });
    },
    [navigate]
  );

  return (
    <Page fullWidth>
      <TabTitle title="Intake" />
      <Toasts toast={toast} setToast={setToast} errorToast={toastError} setErrorToast={setToastError} toastContent="Saved" />
      <Card>
        <Tabs tabs={tabs} selected={tab} onSelect={(index) => update(index, lane)}>
          <div style={{ paddingTop: 12 }}>
            <InlineStack align="space-between" blockAlign="center">
              <ButtonGroup variant="segmented">
                {LANES.map((item) => (
                  <Button key={item.key} pressed={lane === item.key} onClick={() => update(tab, item.key)} size="slim">
                    {`${item.label}${counts[item.countKey] ? ` (${counts[item.countKey]})` : ''}`}
                  </Button>
                ))}
              </ButtonGroup>
              <Button size="slim" variant="plain" url="/approvals">
                My approval queue
              </Button>
            </InlineStack>
            <div style={{ paddingTop: 12 }}>
              <QueueShell
                key={`${docType}-${lane}`}
                docType={docType}
                source="intake"
                lane={lane}
                initialId={initialId}
                emptyTitle="Nothing to process"
                emptyText={lane === 'rejected' ? 'No rejected documents.' : lane === 'to_assign' ? 'Every processed document has an approver.' : 'No new documents arrived.'}
                onDecided={() => refreshSummary()}
                setToast={setToast}
                setErrorToast={setToastError}
                renderCard={({ key, ...props }) => <IntakeCard key={key} {...props} />}
              />
            </div>
          </div>
        </Tabs>
      </Card>
    </Page>
  );
}
